import {
  Box,
  Grid,
  Link,
  FormControl,
  InputLabel,
  Select,
  type SelectChangeEvent,
  Typography,
  MenuItem,
  Snackbar,
  Alert,
  type AlertColor,
} from "@mui/material";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import React, { useEffect, useState } from "react";
import { Label } from "@/components/Label";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { SizingWrapperStyle } from "@/components/Wrapper";
import {
  Button,
  ButtonColorOption,
  ButtonVariantOption,
} from "@/components/Button";
import { useNavigate, useParams } from "react-router-dom";
import {
  type CustomerDetailResponse,
  useCustomerDetail,
} from "../hooks/useCustomerDetail";
import { KeyboardArrowLeft } from "@mui/icons-material";
import TextArea from "@/components/TextArea/TextArea";
import { TextFieldEmail } from "@/components/TextField/TextFieldEmail";
import { TextFieldTell } from "@/components/TextField/TextFieldTell";
import { TextFieldReadOnly } from "@/components/TextField";
import CustomerDeleteDialog from "../components/CustomerDeleteDialog";
import { RealEstateOwnerStatusLabels } from "@/types/realEstateOwner";

export const ExcludedCustomerDetail: React.FC = () => {
  const { id } = useParams();
  const customerId = Number(id);
  const navigate = useNavigate();

  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = (): void => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 顧客情報, 保存, 削除の処理を行うカスタムフック
  const { data, handleSubmit, handleDelete } = useCustomerDetail(customerId);

  // 顧客情報を保持するstate
  const [customerData, setCustomerData] = useState<CustomerDetailResponse>({
    realEstateOwnerId: data?.realEstateOwnerId ?? customerId,
    realEstateOwnerCustomerManagementId:
      data?.realEstateOwnerCustomerManagementId ?? null,
    status: data?.status ?? null,
    realEstateType: data?.realEstateType ?? null,
    originalShozai: data?.originalShozai ?? null,
    postcode: data?.postcode ?? null,
    ownerAddress: data?.ownerAddress ?? "",
    ownerName: data?.ownerName ?? "",
    legalAffairsBureauRequestDate: data?.legalAffairsBureauRequestDate ?? null,
    mochibunStr: data?.mochibunStr ?? "",
    receptionReason: data?.receptionReason ?? null,
    email: data?.email ?? null,
    tel: data?.tel ?? null,
    memo: data?.memo ?? null,
    responsiblePersonName: data?.responsiblePersonName ?? "",
    isOutOfTarget: data?.isOutOfTarget ?? false,
    emailHistories: data?.emailHistories ?? [],
  });

  // 顧客情報の変更
  const onChangeCustomerData = (
    key: keyof CustomerDetailResponse,
    value: string
  ): void => {
    setCustomerData((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    setCustomerData({
      realEstateOwnerId: data?.realEstateOwnerId ?? customerId,
      realEstateOwnerCustomerManagementId:
        data?.realEstateOwnerCustomerManagementId ?? null,
      status: data?.status ?? null,
      realEstateType: data?.realEstateType ?? null,
      originalShozai: data?.originalShozai ?? null,
      postcode: data?.postcode ?? null,
      ownerAddress: data?.ownerAddress ?? "",
      ownerName: data?.ownerName ?? "",
      legalAffairsBureauRequestDate:
        data?.legalAffairsBureauRequestDate ?? null,
      mochibunStr: data?.mochibunStr ?? "",
      receptionReason: data?.receptionReason ?? null,
      email: data?.email ?? null,
      tel: data?.tel ?? null,
      memo: data?.memo ?? null,
      responsiblePersonName: data?.responsiblePersonName ?? "",
      isOutOfTarget: data?.isOutOfTarget ?? false,
      emailHistories: data?.emailHistories ?? [],
    });
  }, [data, customerId]);

  return (
    <PagePaper>
      <form>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* ページタイトル */}
          <Box sx={{ display: "flex", mb: 3 }}>
            <ManageSearchIcon sx={{ mr: 1 }} fontSize="large" />
            <PageTitle>顧客詳細</PageTitle>
          </Box>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ top: { sm: 80, xs: 65 } }} // ヘッダーの高さを考慮し調整
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>

        <SectionPaper>
          <Box sx={{ m: 4 }}>
            <Box sx={{ mx: 2, mt: 2, mb: 1 }}>
              <Label>物件情報</Label>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextFieldReadOnly
                    label="所在+地番"
                    value={customerData?.originalShozai ?? ""}
                    size={SizingWrapperStyle.INHERIT}
                    disabled
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextFieldReadOnly
                    label="登記原因"
                    value={customerData?.receptionReason ?? ""}
                    size={SizingWrapperStyle.INHERIT}
                    disabled
                  />
                </Grid>

                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel id="select-label">ステータス</InputLabel>
                    <Select
                      labelId="select-label"
                      label="ステータス"
                      value={customerData?.status ?? ""}
                      onChange={(event: SelectChangeEvent<string>) => {
                        onChangeCustomerData("status", event.target.value);
                      }}
                    >
                      {Object.entries(RealEstateOwnerStatusLabels).map(
                        ([key, label]) => (
                          <MenuItem key={key} value={key}>
                            {label}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mx: 2, mt: 2, mb: 1 }}>
              <Label>顧客情報</Label>
              <Grid container>
                <Box sx={{ mr: 1 }}>
                  <TextFieldEmail
                    id="email"
                    label="連絡先メールアドレス"
                    value={customerData?.email ?? ""}
                    onChange={(value) => {
                      onChangeCustomerData("email", value);
                    }}
                    required={false}
                  />
                </Box>
                <TextFieldTell
                  id="tel"
                  label="電話番号"
                  value={customerData?.tel ?? ""}
                  onChange={(value) => {
                    onChangeCustomerData("tel", value);
                  }}
                  required={false}
                />
              </Grid>
            </Box>

            <Box sx={{ mx: 2, mt: 2, mb: 1 }}>
              <Label>営業情報</Label>
              <Grid container>
                <TextArea
                  minRows={6}
                  maxRows={10}
                  style={{ width: "100%" }}
                  aria-label="営業メモ"
                  placeholder="営業メモをここに記載してください。"
                  defaultValue={customerData?.memo ?? ""}
                  onChange={(value) => {
                    onChangeCustomerData("memo", value);
                  }}
                />
              </Grid>
            </Box>

            <Box
              sx={{
                mr: 2,
                my: 3,
                display: "flex",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Button
                label="保存"
                disabled={!customerData.status}
                onClick={() => {
                  (async () => {
                    try {
                      await handleSubmit(customerId, customerData);
                      setSnackbar({
                        open: true,
                        message: "保存が完了しました。",
                        severity: "success",
                      });
                    } catch (error) {
                      console.error("保存に失敗しました。", error);
                      setSnackbar({
                        open: true,
                        message: "保存に失敗しました。",
                        severity: "error",
                      });
                    }
                  })();
                }}
                variant={ButtonVariantOption.Contained}
                size={SizingWrapperStyle.SMALL}
              />
              <Button
                label="削除"
                onClick={() => {
                  setOpenDialog(true);
                }}
                variant={ButtonVariantOption.Outlined}
                color={ButtonColorOption.Error}
                size={SizingWrapperStyle.SMALL}
              />
            </Box>
          </Box>
        </SectionPaper>

        <Box sx={{ mt: 2 }}>
          <Link
            href="/customermanagement/excludedcustomerlist"
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>対象外顧客一覧に戻る</Typography>
            </Box>
          </Link>
        </Box>
      </form>

      {/* 削除確認ダイアログ */}
      <CustomerDeleteDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
        }}
        onConfirm={() => {
          setOpenDialog(false);
          (async () => {
            try {
              await handleDelete(
                customerId,
                Number(customerData.realEstateOwnerCustomerManagementId)
              );
              navigate("/customermanagement/excludedcustomerlist");
            } catch (error) {
              console.error("削除に失敗しました。", error);
            }
          })();
        }}
      />
    </PagePaper>
  );
};
